import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { TrendingUp, Users, DollarSign, Calendar, MapPin, Phone, FileText } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useCoordinatorFarms, useCoordinatorStats } from '@/hooks/use-coordinator';
import { useCoordinatorProcurementStats, useCoordinatorFarmerRegistrations } from '@/hooks/use-coordinator-procurements';

const CoordinatorAnalytics: React.FC = () => {
  const { data: farms = [] } = useCoordinatorFarms();
  const { data: farmStats } = useCoordinatorStats();
  const { data: procurementStats } = useCoordinatorProcurementStats();
  const { data: registrations = [] } = useCoordinatorFarmerRegistrations();

  // Calculate additional metrics
  const recentRegistrations = registrations.filter(reg => {
    const regDate = new Date(reg.registration_date);
    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);
    return regDate >= weekAgo;
  }).length;

  const averageFarmSize = farms.length > 0 
    ? farms.reduce((sum, farm) => sum + (farm.land_size || 0), 0) / farms.length
    : 0;

  const topRegions = farms.reduce((acc, farm) => {
    acc[farm.region] = (acc[farm.region] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const sortedRegions = Object.entries(topRegions)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 3);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">Performance Analytics</h2>
          <p className="text-muted-foreground">Your coordinator performance metrics and insights</p>
        </div>
        <Button asChild variant="outline">
          <Link to="/coordinator-reports">
            <TrendingUp className="h-4 w-4 mr-2" />
            View Full Reports
          </Link>
        </Button>
      </div>

      {/* Key Performance Indicators */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <Users className="h-5 w-5 text-blue-600" />
              <div>
                <div className="text-2xl font-bold">{farmStats?.totalFarms || 0}</div>
                <p className="text-sm text-muted-foreground">Registered Farmers</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <Calendar className="h-5 w-5 text-green-600" />
              <div>
                <div className="text-2xl font-bold">{recentRegistrations}</div>
                <p className="text-sm text-muted-foreground">This Week</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <DollarSign className="h-5 w-5 text-purple-600" />
              <div>
                <div className="text-2xl font-bold">
                  {procurementStats?.totalValue ? `$${Math.round(procurementStats.totalValue).toLocaleString()}` : '$0'}
                </div>
                <p className="text-sm text-muted-foreground">Procurement Value</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <TrendingUp className="h-5 w-5 text-orange-600" />
              <div>
                <div className="text-2xl font-bold">{Math.round(averageFarmSize)}</div>
                <p className="text-sm text-muted-foreground">Avg Farm Size (ha)</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <Phone className="h-5 w-5 text-emerald-600" />
              <div>
                <div className="text-2xl font-bold">
                  {farmStats?.totalFarms ? Math.round((farmStats.farmsWithPhoneAccess / farmStats.totalFarms) * 100) : 0}%
                </div>
                <p className="text-sm text-muted-foreground">Phone Access</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Regional Distribution and Recent Activity */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Top Regions</CardTitle>
            <CardDescription>Farmer distribution by region</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {sortedRegions.length > 0 ? (
                sortedRegions.map(([region, count]) => (
                  <div key={region} className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <MapPin className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">{region}</span>
                    </div>
                    <Badge variant="secondary">{count} farmers</Badge>
                  </div>
                ))
              ) : (
                <p className="text-sm text-muted-foreground">No data available</p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Farmer Engagement</CardTitle>
            <CardDescription>Connection and contract statistics</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Phone className="h-4 w-4 text-green-600" />
                  <span>Phone Access</span>
                </div>
                <div className="text-right">
                  <span className="font-medium">{farmStats?.farmsWithPhoneAccess || 0}</span>
                  <span className="text-sm text-muted-foreground"> / {farmStats?.totalFarms || 0}</span>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <FileText className="h-4 w-4 text-blue-600" />
                  <span>Contracts Signed</span>
                </div>
                <div className="text-right">
                  <span className="font-medium">{farmStats?.farmsWithContracts || 0}</span>
                  <span className="text-sm text-muted-foreground"> / {farmStats?.totalFarms || 0}</span>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <TrendingUp className="h-4 w-4 text-purple-600" />
                  <span>Recent Activity</span>
                </div>
                <div className="text-right">
                  <span className="font-medium">{farmStats?.recentRegistrations || 0}</span>
                  <span className="text-sm text-muted-foreground"> this week</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Farmer Registrations */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Farmer Registrations</CardTitle>
          <CardDescription>Latest farmers you've registered</CardDescription>
        </CardHeader>
        <CardContent>
          {registrations.length > 0 ? (
            <div className="space-y-3">
              {registrations.slice(0, 5).map((registration: any) => (
                <div key={registration.id} className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <div>
                      <p className="font-medium">{registration.farmer?.farmer_name}</p>
                      <p className="text-sm text-muted-foreground">
                        {registration.farmer?.region} • {registration.farmer?.product_name}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium">
                      {new Date(registration.registration_date).toLocaleDateString()}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {registration.farmer?.phone_number}
                    </p>
                  </div>
                </div>
              ))}
              
              {registrations.length > 5 && (
                <div className="text-center pt-4">
                  <Button asChild variant="outline">
                    <Link to="/coordinators/farmers">
                      View All Farmers ({registrations.length})
                    </Link>
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8">
              <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-muted-foreground mb-4">No farmer registrations found</p>
              <Button asChild>
                <Link to="/farm-form">
                  Register First Farmer
                </Link>
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CoordinatorAnalytics;